// gigService.js - All Gig Operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// CREATE GIG
// ============================================

export const createGig = async (artistId, gigData) => {
  try {
    const gigId = `gig_${Date.now()}`;
    const gigRef = doc(db, 'liveGigs', gigId);
    
    await setDoc(gigRef, {
      ...gigData,
      id: gigId,
      artistId,
      status: gigData.status || 'upcoming',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Gig created:', gigId);
    return { success: true, gigId };
  } catch (error) {
    console.error('❌ Error creating gig:', error);
    throw error;
  }
};

// ============================================
// UPDATE GIG
// ============================================

export const updateGig = async (gigId, updates) => {
  try {
    const gigRef = doc(db, 'liveGigs', gigId);
    await updateDoc(gigRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    console.log('✅ Gig updated:', gigId);
    return { success: true };
  } catch (error) {
    console.error('❌ Error updating gig:', error);
    throw error;
  }
};

// ============================================
// DELETE GIG
// ============================================

export const deleteGig = async (gigId) => {
  try {
    const gigRef = doc(db, 'liveGigs', gigId);
    await deleteDoc(gigRef);
    
    console.log('✅ Gig deleted:', gigId);
    return { success: true };
  } catch (error) {
    console.error('❌ Error deleting gig:', error);
    throw error;
  }
};

// ============================================
// GET GIG
// ============================================

export const getGig = async (gigId) => {
  try {
    const gigRef = doc(db, 'liveGigs', gigId);
    const gigSnap = await getDoc(gigRef);
    
    if (gigSnap.exists()) {
      return { id: gigSnap.id, ...gigSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error getting gig:', error);
    throw error;
  }
};

// ============================================
// GET ARTIST GIGS
// ============================================

export const getArtistGigs = async (artistId, status = null) => {
  try {
    let q;
    
    if (status) {
      q = query(
        collection(db, 'liveGigs'),
        where('artistId', '==', artistId),
        where('status', '==', status),
        orderBy('gigDate', 'desc')
      );
    } else {
      q = query(
        collection(db, 'liveGigs'),
        where('artistId', '==', artistId),
        orderBy('gigDate', 'desc')
      );
    }
    
    const snapshot = await getDocs(q);
    const gigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return gigs;
  } catch (error) {
    console.error('❌ Error getting artist gigs:', error);
    throw error;
  }
};

// ============================================
// GET LIVE GIGS (Near Location)
// ============================================

export const getLiveGigs = async (userLocation = null, radiusKm = 1) => {
  try {
    const q = query(
      collection(db, 'liveGigs'),
      where('status', '==', 'live')
    );
    
    const snapshot = await getDocs(q);
    let gigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by location if provided
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      gigs = gigs.filter(gig => {
        if (!gig.location || !gig.location.latitude) return false;
        
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          gig.location.latitude,
          gig.location.longitude
        );
        
        return distance <= radiusKm;
      });
    }
    
    return gigs;
  } catch (error) {
    console.error('❌ Error getting live gigs:', error);
    throw error;
  }
};

// ============================================
// LISTEN TO LIVE GIGS (Real-time)
// ============================================

export const listenToLiveGigs = (callback, userLocation = null, radiusKm = 1) => {
  const q = query(
    collection(db, 'liveGigs'),
    where('status', '==', 'live')
  );
  
  return onSnapshot(q, (snapshot) => {
    let gigs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by location if provided
    if (userLocation && userLocation.latitude && userLocation.longitude) {
      gigs = gigs.filter(gig => {
        if (!gig.location || !gig.location.latitude) return false;
        
        const distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          gig.location.latitude,
          gig.location.longitude
        );
        
        return distance <= radiusKm;
      });
    }
    
    callback(gigs);
  });
};

// ============================================
// GO LIVE / END GIG
// ============================================

export const goLive = async (gigId) => {
  return await updateGig(gigId, { 
    status: 'live',
    liveStartedAt: serverTimestamp()
  });
};

export const endGig = async (gigId) => {
  return await updateGig(gigId, { 
    status: 'completed',
    liveEndedAt: serverTimestamp()
  });
};

// ============================================
// HELPER: Calculate Distance
// ============================================

function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees) {
  return degrees * (Math.PI / 180);
}
